import type {
  MarketInstrumentSearchGatewayResult,
  MarketQuoteGateway,
  MarketQuoteGatewayResult,
} from "../application/marketQuoteGateway";
import {
  validateMarketInstrumentSearchResponseV1,
  validateMarketQuoteResponseV1,
  type MarketInstrumentSearchRequestV1,
  type MarketQuoteRequestV1,
  type MarketQuoteResponseV1,
} from "../domain/marketQuote";

const DEFAULT_TIMEOUT_MILLISECONDS = 8_000;
const MAX_RESPONSE_BYTES = 128 * 1024;

export interface FetchMarketQuoteGatewayOptions {
  readonly endpoint?: string;
  readonly searchEndpoint?: string;
  readonly fetch?: typeof globalThis.fetch;
  readonly isOnline?: () => boolean;
  readonly timeoutMs?: number;
}

function retryAfterSeconds(response: Response): number | undefined {
  const retry = Number(response.headers.get("Retry-After"));
  return Number.isInteger(retry) && retry > 0 && retry <= 3_600
    ? retry
    : undefined;
}

function responseMatchesRequest(
  request: MarketQuoteRequestV1,
  response: MarketQuoteResponseV1,
): boolean {
  if (response.results.length !== request.items.length) {
    return false;
  }
  const requestById = new Map(
    request.items.map((item) => [item.requestId, item]),
  );
  return response.results.every((result) => {
    const item = requestById.get(result.requestId);
    return item !== undefined && result.inputTicker === item.ticker;
  });
}

export class FetchMarketQuoteGateway implements MarketQuoteGateway {
  readonly #endpoint: string;
  readonly #fetch: typeof globalThis.fetch;
  readonly #isOnline: () => boolean;
  readonly #searchEndpoint: string;
  readonly #timeoutMs: number;

  constructor(options: FetchMarketQuoteGatewayOptions = {}) {
    this.#endpoint = options.endpoint ?? "/api/quotes";
    this.#searchEndpoint = options.searchEndpoint ?? "/api/quotes/search";
    this.#fetch = options.fetch ?? globalThis.fetch.bind(globalThis);
    this.#isOnline =
      options.isOnline ??
      (() =>
        typeof navigator === "undefined" || navigator.onLine !== false);
    this.#timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MILLISECONDS;
    if (!Number.isFinite(this.#timeoutMs) || this.#timeoutMs <= 0) {
      throw new RangeError("timeoutMs must be a positive finite number");
    }
  }

  async lookup(
    request: MarketQuoteRequestV1,
    externalSignal?: AbortSignal,
  ): Promise<MarketQuoteGatewayResult> {
    if (!this.#isOnline()) {
      return { success: false, reason: "offline" };
    }
    if (externalSignal?.aborted) {
      return { success: false, reason: "aborted" };
    }

    const controller = new AbortController();
    const abortFromExternal = () => controller.abort();
    externalSignal?.addEventListener("abort", abortFromExternal, {
      once: true,
    });
    let timedOut = false;
    const timeout = globalThis.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, this.#timeoutMs);

    try {
      const response = await this.#fetch(this.#endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(request),
        credentials: "same-origin",
        signal: controller.signal,
      });
      if (response.status === 429) {
        return {
          success: false,
          reason: "rate_limited",
          ...(retryAfterSeconds(response)
            ? { retryAfterSeconds: retryAfterSeconds(response) }
            : {}),
        };
      }
      if (!response.ok) {
        return { success: false, reason: "unavailable" };
      }
      const contentLength = Number(response.headers.get("Content-Length"));
      if (
        Number.isFinite(contentLength) &&
        contentLength > MAX_RESPONSE_BYTES
      ) {
        return { success: false, reason: "invalid_response" };
      }
      const text = await response.text();
      if (new TextEncoder().encode(text).byteLength > MAX_RESPONSE_BYTES) {
        return { success: false, reason: "invalid_response" };
      }
      let body: unknown;
      try {
        body = JSON.parse(text) as unknown;
      } catch {
        return { success: false, reason: "invalid_response" };
      }
      const validated = validateMarketQuoteResponseV1(body);
      return validated.success &&
        responseMatchesRequest(request, validated.data)
        ? { success: true, response: validated.data }
        : { success: false, reason: "invalid_response" };
    } catch {
      if (externalSignal?.aborted) {
        return { success: false, reason: "aborted" };
      }
      return {
        success: false,
        reason: timedOut ? "timeout" : "unavailable",
      };
    } finally {
      globalThis.clearTimeout(timeout);
      externalSignal?.removeEventListener("abort", abortFromExternal);
    }
  }

  async search(
    request: MarketInstrumentSearchRequestV1,
    externalSignal?: AbortSignal,
  ): Promise<MarketInstrumentSearchGatewayResult> {
    if (!this.#isOnline()) {
      return { success: false, reason: "offline" };
    }
    if (externalSignal?.aborted) {
      return { success: false, reason: "aborted" };
    }

    const controller = new AbortController();
    const abortFromExternal = () => controller.abort();
    externalSignal?.addEventListener("abort", abortFromExternal, {
      once: true,
    });
    let timedOut = false;
    const timeout = globalThis.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, this.#timeoutMs);

    try {
      const response = await this.#fetch(this.#searchEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(request),
        credentials: "same-origin",
        signal: controller.signal,
      });
      if (response.status === 429) {
        return {
          success: false,
          reason: "rate_limited",
          ...(retryAfterSeconds(response)
            ? { retryAfterSeconds: retryAfterSeconds(response) }
            : {}),
        };
      }
      if (!response.ok) {
        return { success: false, reason: "unavailable" };
      }
      const contentLength = Number(response.headers.get("Content-Length"));
      if (
        Number.isFinite(contentLength) &&
        contentLength > MAX_RESPONSE_BYTES
      ) {
        return { success: false, reason: "invalid_response" };
      }
      const text = await response.text();
      if (new TextEncoder().encode(text).byteLength > MAX_RESPONSE_BYTES) {
        return { success: false, reason: "invalid_response" };
      }
      let body: unknown;
      try {
        body = JSON.parse(text) as unknown;
      } catch {
        return { success: false, reason: "invalid_response" };
      }
      const validated = validateMarketInstrumentSearchResponseV1(body);
      return validated.success && validated.data.query === request.query.trim()
        ? { success: true, response: validated.data }
        : { success: false, reason: "invalid_response" };
    } catch {
      if (externalSignal?.aborted) {
        return { success: false, reason: "aborted" };
      }
      return {
        success: false,
        reason: timedOut ? "timeout" : "unavailable",
      };
    } finally {
      globalThis.clearTimeout(timeout);
      externalSignal?.removeEventListener("abort", abortFromExternal);
    }
  }
}

export function createFetchMarketQuoteGateway(
  options: FetchMarketQuoteGatewayOptions = {},
): MarketQuoteGateway {
  return new FetchMarketQuoteGateway(options);
}
